/**
 * Report job monitor - real-time progress via SSE
 */

import React from 'react';
import { Card } from '../UI/Card';
import { Badge } from '../UI/Badge';
import { Button } from '../UI/Button';
import { LoadingSpinner } from '../UI/LoadingSpinner';
import { useReportJobStatus } from '../../hooks/useReportJob';
import { CheckCircle, XCircle, Clock, FileText, ExternalLink } from 'lucide-react';

export interface ReportJobMonitorProps {
  jobId: string;
  onComplete?: (reportId: string) => void;
  showViewButton?: boolean;
  onViewReport?: (reportId: string) => void;
}

export function ReportJobMonitor({ 
  jobId, 
  onComplete, 
  showViewButton = true,
  onViewReport 
}: ReportJobMonitorProps) {
  const { data: job, isLoading } = useReportJobStatus(jobId);

  React.useEffect(() => {
    if (job?.status === 'completed' && job.output_data?.report_id) {
      onComplete?.(job.output_data.report_id);
    }
  }, [job, onComplete]);

  if (isLoading || !job) {
    return <LoadingSpinner message="Loading job status..." />;
  }

  const progress = job.total_questions > 0
    ? (job.questions_complete / job.total_questions) * 100
    : 0;

  const statusColor = {
    pending: 'warning',
    queued: 'warning',
    running: 'info',
    completed: 'success',
    failed: 'error',
    checkpointed: 'warning',
  } as const;

  const statusMessage = job.progress_data?.status_message || 
    (job.status === 'completed' ? 'Report generation complete!' :
     job.status === 'running' ? `Processing question ${job.questions_complete + 1} of ${job.total_questions}` :
     job.status === 'checkpointed' ? 'Job checkpointed - will continue in background' :
     `Status: ${job.status}`);

  const handleViewReport = () => {
    if (job.output_data?.report_id) {
      if (onViewReport) {
        onViewReport(job.output_data.report_id);
      } else {
        // Default: navigate to proposal management dashboard
        window.location.href = `/rfp-agent/proposals?reportId=${job.output_data.report_id}`;
      }
    }
  };

  return (
    <Card title="Report Generation Progress">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Badge variant={statusColor[job.status as keyof typeof statusColor] || 'warning'}>
            {job.status.toUpperCase()}
          </Badge>
          <span className="text-sm text-gray-600">
            {job.questions_complete} / {job.total_questions} questions
          </span>
        </div>

        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-primary-600 h-2 rounded-full transition-all"
            style={{ width: `${Math.min(progress, 100)}%` }}
          />
        </div>

        <div className="text-sm">
          <p className="text-gray-700 dark:text-gray-300 font-medium mb-1">{statusMessage}</p>
          {job.current_section && (
            <p className="text-gray-600 dark:text-gray-400">
              Current Section: <span className="font-medium">{job.current_section}</span>
            </p>
          )}
          {job.current_question && (
            <p className="text-gray-500 dark:text-gray-500 mt-1 text-xs line-clamp-2">
              {job.current_question}
            </p>
          )}
        </div>

        <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
          <div className="flex items-center gap-1">
            <CheckCircle className="h-4 w-4 text-green-500" />
            <span>Cache hits: {job.cache_hits || 0}</span>
          </div>
          <div className="flex items-center gap-1">
            <XCircle className="h-4 w-4 text-gray-400" />
            <span>Cache misses: {job.cache_misses || 0}</span>
          </div>
          {job.eta_seconds && job.eta_seconds > 0 && (
            <div className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              <span>ETA: {Math.ceil(job.eta_seconds / 60)}m</span>
            </div>
          )}
          {job.progress_data?.elapsed_seconds && (
            <div className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              <span>Elapsed: {Math.floor(job.progress_data.elapsed_seconds / 60)}m {job.progress_data.elapsed_seconds % 60}s</span>
            </div>
          )}
        </div>

        {job.status === 'completed' && job.output_data?.report_id && showViewButton && (
          <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
            <Button
              onClick={handleViewReport}
              className="w-full"
            >
              <FileText className="h-4 w-4 mr-2" />
              View Proposal
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}


